/* A PAGE'S STYLE — how it looks, and therefore how much room its words have.
 *
 * Her call, 2026-10-16 (docs/sites-pictures-spec.md §0): a page starts from
 * TWO choices. The STYLE decides how it looks and where the pictures go; her
 * WORTHI category (utils/worthi.js) decides what the page leans on. Four
 * styles, Light & Feminine first because it fits about 80% of her clients.
 *
 * WHY A STYLE CARRIES WORD COUNTS. Her words: "the amount of text … is
 * completely dependent on photos … things may need to be just three lines to
 * fit into a section or a grid. so it changes how many words should be
 * generated." A banner beside a tall photo has half the width of one with no
 * picture; an airy page wants fewer words in every box than a structured one.
 *
 * HOW THE NUMBERS REACH EVERYTHING — one seam, not a new one. A section's
 * word budgets already come from `fieldWords(field, limits)`: her own number
 * for that field if she set one, else the kind's default. The style's room is
 * slipped in UNDERNEATH her numbers (`limitsFor`), so the counter under the
 * field, the number she clicks to change and the number Brill is told stay
 * the one fact — and her own number still wins. Nothing is stored: the room
 * is worked out from the page's style each time, and `onLimits` keeps writing
 * only what she typed.
 *
 * NO STYLE, NO CHANGE. A page with no style picked gets no room at all, so
 * every page that existed before this keeps exactly the numbers it had.
 */

export const SITE_STYLES = [
  {
    id: "light_feminine",
    label: "Light & Feminine",
    words: ["light", "airy", "elegant", "soft"],
    look: "a light blush or sage background, an elegant serif for headlines with one line set in italic, a clean sans-serif for text, the photo tall beside the words, soft circles in the background, quiet buttons, a lot of white space",
    corners: "barely rounded corners (about 4px)",
  },
  {
    id: "bold",
    label: "Bold",
    words: ["dark", "confident", "modern", "high contrast"],
    look: "a dark banner, big confident headlines, one highlighted word per headline, a graphic beside the words, strong solid buttons",
    corners: "slightly rounded corners (about 10px)",
  },
  {
    id: "classic",
    label: "Classic",
    words: ["polished", "structured", "calm", "square"],
    look: "a wide photo behind the banner words, a structured grid, a band of facts, clear numbered steps, restrained colour",
    corners: "square corners",
  },
  {
    id: "artistic",
    label: "Artistic",
    words: ["expressive", "textured", "asymmetric", "earthy"],
    look: "oversized type, pictures layered at a slight angle, blocks of colour, an asymmetric layout with room to breathe",
    corners: "no rounding on pictures, soft rounding on buttons",
  },
];

export const DEFAULT_STYLE = "light_feminine";

/** The style a page has picked, or null — never a default. A page with no
 *  style keeps the numbers it always had (see the note at the top). */
export function styleDef(id) {
  return SITE_STYLES.find((s) => s.id === id) || null;
}

/* THE ROOM, by style and section kind, in words. Keys are the FIELD keys of
   websiteSectionKinds.js, part keys included (`line` in Services, `q` and `a`
   in FAQ), because that is what `fieldWords` looks a number up by. Note that
   `head` is the banner's HEADLINE and every other kind's HEADING — the same
   key, two jobs — which is why the room is set per kind over a base.

   A testimonial's quote is never given a room: it is somebody else's words,
   picked word for word, and a budget on it reads as an invitation to trim.
   That is also why Other's copy is set under `other` and never in `base`: an
   Other section's copy and a testimonial's quote are both keyed `text`. */
const ROOM = {
  light_feminine: {
    base: { head: 6, sub: 18, btn: 4, paras: 40, line: 14, q: 10, a: 40 },
    other: { heading: 6, text: 45 },
    banner: { eyebrow: 3, head: 14, sub: 16, btn: 4 },
  },
  bold: {
    base: { head: 5, sub: 18, btn: 4, paras: 45, line: 12, q: 10, a: 40 },
    other: { heading: 5, text: 40 },
    banner: { eyebrow: 3, head: 7, sub: 20, btn: 5 },
  },
  classic: {
    base: { head: 6, sub: 25, btn: 4, paras: 55, line: 18, q: 12, a: 50 },
    other: { heading: 6, text: 60 },
    banner: { eyebrow: 4, head: 10, sub: 25, btn: 4 },
  },
  artistic: {
    base: { head: 4, sub: 14, btn: 3, paras: 35, line: 10, q: 10, a: 35 },
    other: { heading: 4, text: 35 },
    banner: { eyebrow: 2, head: 6, sub: 14, btn: 3 },
  },
};

/** The room a style gives one kind of section, as { fieldKey: words }.
 *  Empty for no style or an unknown one. */
export function roomFor(styleId, kindId) {
  const r = ROOM[styleId];
  if (!r) return {};
  const kind = String(kindId || "").toLowerCase();
  return { ...r.base, ...(r[kind] || {}) };
}

/**
 * The word budgets one section is written to: the style's room UNDER her own
 * numbers. Hand this wherever `section.limits` used to go.
 *
 * @param section  a stored section — reads `sectionType` and `limits`
 * @param styleId  the page's style, or null
 */
export function limitsFor(section, styleId) {
  const room = roomFor(styleId, section?.sectionType);
  const mine = section?.limits || {};
  if (!Object.keys(room).length) return section?.limits;
  return { ...room, ...mine };
}

/** The line Brill is sent about a styled page. Null when there is no style,
 *  so an unstyled page's context is exactly what it was. */
export function styleLine(styleId) {
  const s = styleDef(styleId);
  if (!s) return null;
  return `Page style: ${s.label} (${s.words.join(", ")}). The word counts below are the ROOM each field has in this layout, not advice: land at them or under. A picture or graphic sits beside most sections and carries part of the message, so the words should not describe it.`;
}
